// apps/smart-shikka-web/src/pages/SuperAdmin.tsx
// Sample super-admin panel: real useful settings only, no dummy numbers.
import { useState } from "react";
import { useAuth } from "../auth.js";
import { AppNav } from "../components/AppNav.js";
import { useBrandColors } from "@smart-shikka/react";
import { useLocale } from "../ui.js";

type Section = "organization" | "members" | "billing" | "apiKeys" | "connectors" | "sessionManagement" | "droidComputers" | "sessionDefaults" | "missionDefaults" | "customModels" | "usage" | "support";

const SECTIONS: { key: Section; labelKey: string }[] = [
  { key: "organization", labelKey: "organization" },
  { key: "members", labelKey: "members" },
  { key: "billing", labelKey: "billing" },
  { key: "apiKeys", labelKey: "apiKeys" },
  { key: "connectors", labelKey: "connectors" },
  { key: "sessionManagement", labelKey: "sessionManagement" },
  { key: "droidComputers", labelKey: "droidComputers" },
  { key: "sessionDefaults", labelKey: "sessionDefaults" },
  { key: "missionDefaults", labelKey: "missionDefaults" },
  { key: "customModels", labelKey: "customModels" },
  { key: "usage", labelKey: "usage" },
  { key: "support", labelKey: "support" },
];

export function SuperAdminPage() {
  // PR #767: the full management panel is super-admin only. super-sub-admin keeps
  // CEO/Jarvis, Permissions/Trust and Emergency Stop through the account menu.
  const { isOwner } = useAuth();
  const C = useBrandColors();
  const { locale, tr } = useLocale();
  const [section, setSection] = useState<Section>("organization");

  if (!isOwner) {
    return (
      <div style={{ display:"flex", height:"100%", background:C.bg, fontFamily:C.fontSans, alignItems:"center", justifyContent:"center" }}>
        <div style={{ padding:24, border:`1px solid ${C.border}`, borderRadius:12, background:C.card }}>
          <h2 style={{ fontSize:16, color:C.fg, margin:0 }}>{tr("accessDenied")}</h2>
          <p style={{ fontSize:12, color:C.muted, margin:"8px 0 0" }}>{locale === "hi" ? "यह पैनल केवल super-admin के लिए है।" : "This panel is for super-admin only."}</p>
        </div>
      </div>
    );
  }

  const content = (title: string, body: string) => (
    <section style={{ marginBottom:24 }}>
      <h2 style={{ fontSize:14, fontWeight:700, color:C.fg, margin:"0 0 10px" }}>{title}</h2>
      <div style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:"13px 16px", background:C.card }}>
        <p style={{ fontSize:12, color:C.muted, margin:0 }}>{body}</p>
      </div>
    </section>
  );

  const sectionBody = (key: Section) => {
    switch (key) {
      case "organization":
        return content(tr("organization"), locale === "hi" ? "Organization नाम, slug और contact email configure करें। अभी कोई saved value नहीं।" : "Configure organization name, slug and contact email. No saved values yet.");
      case "members":
        return content(tr("members"), locale === "hi" ? "Members invite/role management. Backend route /api/admin/members से आएगा।" : "Member invite and role management. Backend route /api/admin/members will provide this.");
      case "billing":
        return content(tr("billing"), locale === "hi" ? "Billing और plan details. अभी कोई usage charge नहीं; show 'no data' truthfully." : "Billing and plan details. No usage charges yet; shown truthfully as no data.");
      case "apiKeys":
        return content(tr("apiKeys"), locale === "hi" ? "API keys rotate / revoke करें। Current keys list server से fetch होगी।" : "Rotate or revoke API keys. Current keys list will be fetched from server.");
      case "connectors":
        return content(tr("connectors"), locale === "hi" ? "Slack, GitHub, Supabase, MCP connectors यहाँ configure होंगे।" : "Slack, GitHub, Supabase and MCP connectors will be configured here.");
      case "sessionManagement":
        return content(tr("sessionManagement"), locale === "hi" ? "Live session monitoring और Emergency Stop. Runtime state से जुड़ेगा।" : "Live session monitoring and Emergency Stop. Will connect to runtime state.");
      case "droidComputers":
        return content(tr("droidComputers"), locale === "hi" ? "Desktop droid / fleet computers. अभी कोई registered droid नहीं।" : "Desktop droid / fleet computers. No registered droids yet.");
      case "sessionDefaults":
        return content(tr("sessionDefaults"), locale === "hi" ? "Default model, temperature और max tokens।" : "Default model, temperature and max tokens.");
      case "missionDefaults":
        return content(tr("missionDefaults"), locale === "hi" ? "Default mission profile और permission policy।" : "Default mission profile and permission policy.");
      case "customModels":
        return content(tr("customModels"), locale === "hi" ? "Custom fine-tuned model endpoints।" : "Custom fine-tuned model endpoints.");
      case "usage":
        return content(tr("usage"), locale === "hi" ? "Usage और audit logs; no fake numbers." : "Usage and audit logs; no fake numbers.");
      case "support":
        return content(tr("support"), locale === "hi" ? "Support links और diagnostic export।" : "Support links and diagnostic export.");
      default:
        return null;
    }
  };

  return (
    <div style={{ display:"flex", height:"100%", background:C.bg, fontFamily:C.fontSans }}>
      <AppNav activeRoute="admin" />
      <aside style={{ width:200, background:C.panel, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", padding:"12px 8px", gap:2, flexShrink:0 }}>
        {SECTIONS.map(({ key, labelKey }) => {
          const active = section === key;
          return (
            <button key={key} onClick={() => setSection(key)} style={{ textAlign:"left", padding:"7px 10px", borderRadius:6, border:"none", background:active ? C.accent : "transparent", color:active ? C.accentFg : C.fg, fontSize:12, cursor:"pointer", fontWeight:active ? 600 : 400, fontFamily:C.fontSans }}>{tr(labelKey as any)}</button>
          );
        })}
      </aside>
      <main style={{ flex:1, overflow:"auto", padding:"28px 36px" }}>
        <div style={{ maxWidth:720 }}>
          <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>🛡 {tr("adminPanel")}</div>
          <h1 style={{ fontSize:22, fontWeight:700, color:C.fg, margin:"0 0 20px" }}>{tr(SECTIONS.find(s => s.key === section)!.labelKey as any)}</h1>
          {sectionBody(section)}
        </div>
      </main>
    </div>
  );
}
